import React, { useEffect, useState, Suspense, lazy, memo, createElement } from 'react';
import { Check, X, ShieldCheck, Star, Target, Flame, Calendar, Edit3, ChevronDown, ChevronLeft, ChevronRight, Quote, Clock, Zap, BookOpen, GraduationCap, Sparkles, ClipboardList, Users, Heart, Map, AlertCircle, Lightbulb, Infinity as InfinityIcon, Rocket, Layers } from 'lucide-react';
// Lazy load below-the-fold heavy section to reduce initial JS on mobile
const CommentsSection = lazy(() => import('./components/CommentsSection').then(m => ({
  default: m.CommentsSection
})));
// SocialProofPopup is lazy-loaded only AFTER first paint to keep initial JS small
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const CTAButton = ({
  children,
  subtext,
  scrollTo
}: {
  children: React.ReactNode;
  subtext?: string;
  scrollTo?: string;
}) => {
  const handleClick = () => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="flex flex-col items-center w-full max-w-xl mx-auto mt-8 mb-6" data-id="element-0">
      <button onClick={handleClick} className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)]" data-id="element-1">
        <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-2">
          {children}
        </span>
      </button>
      {subtext && <p className="mt-3 text-sm font-medium text-gray-400 flex items-center gap-2 text-center" data-id="element-3">
          {subtext}
        </p>}
    </div>;
};
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/51RguBNyphZQbfS9WXRnhw/ChatGPT_Image_8_may_2026,_17_42_29-Photoroom.webp';
const CHECKOUT_URL = 'https://italia.recursopack.com/cart/40988178776113:1';
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselSlides = [{
    title: 'Adattare i Materiali in Minuti',
    subtitle: 'Tomo 5 — Capitolo 3 · Inclusione e UDL',
    image: 'https://cdn.magicpatterns.com/uploads/fFfFHU6jbYHDvtqk9XZDre/TOMO-6-Gestione-del-Team.pdf_%284%29.webp'
  }, {
    title: 'La Libreria Completa dei Prompt di Valutazione',
    subtitle: 'Tomo 4 — Appendice A · 18 prompt pronti',
    image: 'https://cdn.magicpatterns.com/uploads/1W3z4cpcg1o74Lj12AndNR/TOMO-6-Gestione-del-Team.pdf_%283%29.webp'
  }, {
    title: 'Attività per Infanzia e Primaria',
    subtitle: 'Tomo 3 — Libreria per livello',
    image: 'https://cdn.magicpatterns.com/uploads/32PHdjDniJAnZCjLMkJfFe/TOMO-6-Gestione-del-Team.pdf_%282%29.webp'
  }, {
    title: "Cos'è una UDA e Perché Cambia Tutto",
    subtitle: 'Tomo 2 — Capitolo 2 · Pianificazione',
    image: 'https://cdn.magicpatterns.com/uploads/jHj8dVpJXyRAEaTtcsZFte/TOMO-6-Gestione-del-Team.pdf_%281%29.webp'
  }, {
    title: "L'Architetto dell'Apprendimento",
    subtitle: 'Tomo 1 — La tua nuova identità professionale',
    image: 'https://cdn.magicpatterns.com/uploads/wB5X1VEAssv4RmpeHnTUtc/TOMO-6-Gestione-del-Team.pdf.webp'
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % carouselSlides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  // Resource hints + non-blocking fonts moved to index.tsx (runs BEFORE React mounts).
  // Meta Pixel — deferred to reduce TBT
  useEffect(() => {
    const loadPixel = () => {
      if ((window as any).fbq) return;
      const f = window as any;
      const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
      n('init', '4445454969060534');
      n('track', 'PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadPixel, 2500);
    }
  }, []);
  const faqs = [{
    q: 'È davvero tutto in PDF?',
    a: 'Sì, tutto in PDF scaricabile immediatamente dopo il pagamento. Nessun software, nessuna app, nessun accesso a piattaforme. Lo apri e lo usi su qualsiasi dispositivo — tablet, cellulare o computer.'
  }, {
    q: 'Funziona per la mia materia e il mio livello?',
    a: 'Sì, per qualsiasi materia e qualsiasi livello. Italiano, matematica, scienze, lingue, sostegno, arte, educazione fisica e qualsiasi altra disciplina. Nido, primaria, secondaria, università e formazione professionale.'
  }, {
    q: 'Devo avere competenze particolari?',
    a: 'Zero. Il Tomo 1 parte da zero assoluto. Se sai usare WhatsApp, hai già tutto quello che ti serve per iniziare oggi stesso.'
  }, {
    q: 'Quando ricevo i materiali?',
    a: "Immediatamente dopo il pagamento. Ricevi un'email con il link per scaricare tutto il sistema — 6 tomi e 6 bonus — in meno di 2 minuti."
  }, {
    q: 'E se non sono soddisfatto?',
    a: 'Hai 7 giorni per provare tutto. Se non sei soddisfatto al 100% ti restituiamo ogni centesimo. Nessuna domanda. Nessuna burocrazia.'
  }, {
    q: 'Perché costa così poco?',
    a: 'Perché è un prodotto digitale senza costi di stampa, spedizione o distribuzione fisica. Il prezzo di lancio è bloccato a €19,90 solo per i primi 200 docenti. Poi tornerà al prezzo reale di €47.'
  }];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const tomos = [{
    icon: Sparkles,
    num: '1',
    title: 'Fondamenti e Prompt di Intelligenza Artificiale',
    desc: "Parti da zero e diventa operativo in 7 giorni — anche se non hai mai usato l'intelligenza artificiale in vita tua. Mentalità, configurazione strumenti gratuiti, formula R-C-O-F per prompt perfetti, etica e privacy nel contesto scolastico italiano."
  }, {
    icon: Calendar,
    num: '2',
    title: 'Pianificazione Totale: 365 giorni in 1 ora',
    desc: 'La tua domenica torna libera. Per sempre. Prompt Maestro Annuale, UDA pronte in 20 minuti, Metodo della Domenica Libera (15 minuti il venerdì), calendario 2025-2026 e prompt di emergenza.'
  }, {
    icon: Lightbulb,
    num: '3',
    title: 'Il Laboratorio di Classe Creativa',
    desc: "Trasforma ogni lezione in un'esperienza memorabile. Le 5 fasi emotive, storytelling didattico con intelligenza artificiale, gamification ed escape room pronte, attività collaborative e biblioteca per ogni livello."
  }, {
    icon: ClipboardList,
    num: '4',
    title: 'Correggere meno, valutare meglio',
    desc: 'Dimezza il tempo di correzione senza perdere qualità. Rubriche in 5 minuti, feedback personalizzato in secondi, verifiche differenziate per BES e DSA, comunicazione coi genitori e valutazione formativa.'
  }, {
    icon: Heart,
    num: '5',
    title: 'Il Docente Inclusivo: BES, DSA e Diversità',
    desc: "Quadro normativo italiano completo: BES, DSA, ADHD. Prompt Maestro per PDP completo in meno di un'ora, adattamento materiali per dislessia, disgrafia, discalculia, tecnologie assistive e co-teaching."
  }, {
    icon: Zap,
    num: '6',
    title: 'Produttività Docente: meno stress, più vita',
    desc: 'Hai scelto di insegnare, non di sopravvivere. Gestione email e circolari con intelligenza artificiale, automazione documenti ripetitivi, gestione del tempo, protezione dei week-end e Piano di Benessere Docente.'
  }];
  const bonuses = [{
    icon: BookOpen,
    title: "Bonus 1 — Glossario Visivo dell'Intelligenza Artificiale per Docenti",
    desc: "Tutti i termini dell'intelligenza artificiale spiegati in modo semplice e visivo. Zero gergo tecnico."
  }, {
    icon: Users,
    title: 'Bonus 2 — 30 Frasi Pronte per Parlare di Intelligenza Artificiale',
    desc: "Le espressioni esatte per spiegare l'intelligenza artificiale a colleghi, dirigenti e genitori con sicurezza totale."
  }, {
    icon: Map,
    title: 'Bonus 3 — Mappa Rapida degli Strumenti 2026',
    desc: 'I migliori tool gratuiti e a pagamento già selezionati, testati e categorizzati per uso scolastico reale.'
  }, {
    icon: Check,
    title: 'Bonus 4 — Checklist di Attivazione Rapida',
    desc: 'Inizia subito senza confusione. Ogni passo spiegato in sequenza dal giorno 1.'
  }, {
    icon: AlertCircle,
    title: 'Bonus 5 — Manuale degli Errori che Fanno Perdere Tempo',
    desc: 'Gli errori più comuni del docente digitale italiano e come evitarli dal primo giorno di utilizzo.'
  }, {
    icon: Edit3,
    title: "Bonus 6 — Piano Personale di Adozione dell'Intelligenza Artificiale",
    desc: "Il tuo percorso personalizzato passo dopo passo dal giorno 1 al giorno 30 per integrare l'intelligenza artificiale nella tua routine senza stress."
  }];
  const testimonialsTop = [{
    quote: "Ho pianificato l'intero trimestre in 40 minuti con i prompt del Tomo 2. Non ci credevo fino a quando non l'ho fatto davvero.",
    name: 'Marta R.',
    role: 'Docente di lettere, scuola secondaria, Milano'
  }, {
    quote: "Il Tomo 5 mi ha salvato. Ho creato il PDP per tre studenti BES in meno di un'ora. Prima mi ci volevano giorni interi.",
    name: 'Giuseppe T.',
    role: 'Insegnante di sostegno, Napoli'
  }, {
    quote: 'Finalmente ho una domenica completamente libera. Non succedeva da tre anni. Grazie Docente PRO.',
    name: 'Alessia M.',
    role: 'Maestra di primaria, Roma'
  }];
  const testimonialsBottom = [{
    quote: 'Ho implementato il Metodo della Domenica Libera e in due settimane ho azzerato il lavoro nel week-end. Non ci credevo possibile.',
    name: 'Stefano B.',
    role: 'Professore di scienze, Roma'
  }, {
    quote: "La guida sui prompt mi ha aperto gli occhi. Stavo perdendo 3 ore a settimana su cose che l'intelligenza artificiale fa in 5 minuti.",
    name: 'Giovanna F.',
    role: 'Maestra di primaria, Firenze'
  }, {
    quote: 'Finalmente ho superato un anno scolastico senza stress da burocrazia. Tutto organizzato, tutto documentato, tutto in ordine.',
    name: 'Roberto C.',
    role: 'Docente di sostegno, Torino'
  }];
  const differentiators = [{
    icon: Target,
    title: 'OPERATIVO, NON TEORICO',
    desc: 'Ogni pagina è uno strumento pronto da usare oggi nella tua classe, non un manuale accademico.'
  }, {
    icon: Rocket,
    title: 'IMMEDIATO',
    desc: "Nessun corso da seguire, nessun video da guardare. Apri il PDF, copia il prompt, incollalo nell'intelligenza artificiale, ottieni il risultato."
  }, {
    icon: Layers,
    title: 'COMPLETO',
    desc: 'Copre ogni area del lavoro docente: pianificazione, valutazione, inclusione, burocrazia, lezioni e benessere professionale.'
  }, {
    icon: InfinityIcon,
    title: 'È TUO PER SEMPRE',
    desc: 'Pagamento unico. Nessun abbonamento. Lo usi quando vuoi, come vuoi, su ogni dispositivo.'
  }];
  const TestimonialCard = ({
    quote,
    name,
    role
  }: {
    quote: string;
    name: string;
    role: string;
  }) => <div className="glass-panel p-6 md:p-7 rounded-2xl border-white/10 relative h-full flex flex-col" data-id="element-4">
      <Quote className="w-8 h-8 text-gold-400/40 mb-3 shrink-0" data-id="element-5" />
      <p className="text-gray-200 leading-relaxed mb-5 flex-1 italic" data-id="element-6">
        "{quote}"
      </p>
      <div className="flex items-center gap-1 text-gold-400 mb-2" data-id="element-7">
        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-8" />)}
      </div>
      <p className="text-white font-bold text-sm" data-id="element-9">— {name}</p>
      <p className="text-gray-500 text-xs" data-id="element-10">{role}</p>
    </div>;
  return <div className="min-h-screen bg-dark-900 text-gray-300 selection:bg-gold-500 selection:text-black pb-20 overflow-x-hidden" data-id="element-11">
      {/* Banner — launch offer */}
      <div className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 py-3 px-4 flex justify-center items-center z-50" data-id="element-12">
        <div className="flex items-center gap-3 max-w-4xl mx-auto text-center" data-id="element-13">
          <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse-fast shrink-0 shadow-[0_0_10px_rgba(255,255,255,0.8)]" data-id="element-14"></div>
          <p className="text-xs md:text-sm font-bold tracking-wider text-white uppercase" data-id="element-15">
            OFFERTA LANCIO LIMITATA — SOLO PER I PRIMI 200 DOCENTI · PREZZO
            BLOCCATO A €19,90
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-20" data-id="element-16">
        {/* Hero */}
        <div className="text-center space-y-5 md:space-y-8 mb-8 md:mb-12" data-id="element-17">
          <h1 className="text-3xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight" data-id="element-18">
            Stai ancora sprecando{' '}
            <span className="gold-gradient-text" data-id="element-19">15 ore a settimana</span> per
            pianificare, correggere e fare burocrazia a mano?
          </h1>

          <div className="max-w-3xl mx-auto space-y-4 md:space-y-6" data-id="element-20">
            <div className="relative pl-5 md:pl-6 py-2 text-left" data-id="element-21">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-gold-400 to-gold-600 rounded-full" data-id="element-22"></div>
              <p className="text-base md:text-xl text-gray-300 leading-relaxed font-medium" data-id="element-23">
                Il sistema completo usato dai{' '}
                <span className="text-white font-bold" data-id="element-24">
                  docenti professionisti
                </span>{' '}
                per azzerare la burocrazia, liberare il week-end e insegnare
                davvero — senza corsi costosi, senza software e senza perdere
                altro tempo prezioso.
              </p>
            </div>
            <p className="text-lg md:text-2xl font-bold text-gold-400" data-id="element-25">
              Docente PRO: Sistema IA Universale
            </p>
          </div>
        </div>

        {/* Product image + social proof */}
        <div className="flex flex-col items-center mb-16" data-id="element-26">
          <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl mx-auto mb-8 aspect-square" data-id="element-27">
            <div className="absolute inset-0 bg-gold-500/10 blur-[100px] rounded-full pointer-events-none blur-glow" data-id="element-28"></div>
            <img src={HERO_IMAGE} alt="Docente PRO: Sistema IA Universale Bundle" width={1080} height={1080} fetchPriority="high" decoding="sync" loading="eager" className="relative z-10 w-full h-full object-contain drop-shadow-2xl md:hover:scale-[1.02] transition-transform duration-500" data-id="element-29" />
          </div>

          <div className="flex flex-col items-center space-y-3 mb-8" data-id="element-30">
            <div className="flex items-center gap-1 text-gold-400" data-id="element-31">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-32" />)}
            </div>
            <p className="text-white font-semibold text-lg" data-id="element-33">
              4.9/5 (+2.300 Docenti Soddisfatti)
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 font-medium text-sm text-center" data-id="element-34">
              <Flame className="w-4 h-4 shrink-0" data-id="element-35" />
              89 docenti hanno scelto questo Sistema oggi
            </div>
          </div>

          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia 7 Giorni • 💳 Pagamento Unico" scrollTo="offerta" data-id="element-36">
            ✅ SÌ! VOGLIO IL SISTEMA COMPLETO
          </CTAButton>

          <div className="mt-6 flex flex-col items-center gap-3 text-center" data-id="element-37">
            <div className="flex gap-2 text-2xl" data-id="element-38">📚 ✏️ 🎓 💡 🇮🇹</div>
            <p className="text-sm text-gray-400 max-w-md" data-id="element-39">
              Il sistema N.1 scelto dai docenti italiani di ogni ordine e grado.
            </p>
          </div>
        </div>

        {/* Opening block */}
        <div className="mb-16" data-id="element-40">
          <div className="glass-panel p-8 md:p-10 rounded-2xl border-gold-500/20 relative overflow-hidden" data-id="element-41">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-gold-400 to-gold-600" data-id="element-42"></div>
            <Quote className="absolute top-6 right-6 w-24 h-24 text-white/5 rotate-180 pointer-events-none" data-id="element-43" />
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed relative z-10 font-medium" data-id="element-44">
              <span className="text-white font-bold" data-id="element-45">
                Insegnare in Italia oggi è diventato quasi impossibile da
                sostenere.
              </span>{' '}
              La burocrazia cresce ogni anno, le classi sono sempre più
              complesse, i PDP e le UDA si accumulano, le famiglie chiedono
              sempre di più. Lavori 10-12 ore al giorno e alla fine della
              settimana ti chiedi dove è finito il tuo tempo.{' '}
              <span className="text-gold-400 font-bold" data-id="element-46">Non è colpa tua.</span>{' '}
              Nessuno ti ha mai dato un sistema concreto per gestire tutto in
              modo professionale usando gli strumenti che esistono già —
              gratuitamente. Fino ad oggi.
            </p>
          </div>
        </div>

        {/* Testimonials — top */}
        <div className="mb-20" data-id="element-47">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-8" data-id="element-48">
            Cosa dicono i docenti che lo usano già
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-49">
            {testimonialsTop.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-50" />)}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-20" data-id="element-51">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-52">
            La tua trasformazione
          </h2>
          <div className="glass-panel rounded-2xl overflow-hidden" data-id="element-53">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-54">
              <div className="p-6 md:p-8 bg-dark-800/50 border-b md:border-b-0 md:border-r border-white/5" data-id="element-55">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2" data-id="element-56">
                  SENZA IL SISTEMA
                  <span className="text-sm font-normal text-gray-500 block mt-1" data-id="element-57">
                    (Cosa ti costa oggi)
                  </span>
                </h3>
                <ul className="space-y-6" data-id="element-58">
                  {['Domeniche perse a pianificare e correggere senza fine.', 'Ore sprecate cercando attività su Google da adattare.', 'Stesse relazioni ogni anno, cambiando solo le date.', 'PDP e UDA che mangiano giorni interi del tuo tempo.', 'Correzioni infinite sul tavolo della cucina.', 'Lezioni improvvisate la sera prima.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-59">
                      <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-60">
                        <X className="w-4 h-4 text-red-500" data-id="element-61" />
                      </div>
                      <span className="leading-relaxed" data-id="element-62">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="p-6 md:p-8 bg-emerald-900/10 relative overflow-hidden" data-id="element-63">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[80px] rounded-full pointer-events-none blur-glow" data-id="element-64"></div>
                <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2 relative z-10" data-id="element-65">
                  CON DOCENTE PRO
                  <span className="text-sm font-normal text-emerald-500/70 block mt-1" data-id="element-66">
                    (La tua vita domani)
                  </span>
                </h3>
                <ul className="space-y-6 relative z-10" data-id="element-67">
                  {['Week-end protetti e domeniche davvero libere.', 'Prompt pronti che producono risultati in pochi secondi.', "Documenti scolastici generati e personalizzati dall'intelligenza artificiale.", 'PDP completi in 45 minuti invece che giorni interi.', 'Verifiche dimezzate con feedback automatico.', 'Attività pronte per ogni materia e ogni livello.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white font-medium" data-id="element-68">
                      <div className="mt-1 bg-emerald-500/20 p-1 rounded-full shrink-0" data-id="element-69">
                        <Check className="w-4 h-4 text-emerald-400" data-id="element-70" />
                      </div>
                      <span className="leading-relaxed" data-id="element-71">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Per te se */}
        <div className="mb-20" data-id="element-72">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-73">
            Questo sistema è per te se...
          </h2>
          <div className="glass-panel p-8 rounded-2xl border-white/10" data-id="element-74">
            <ul className="space-y-5" data-id="element-75">
              {['Insegni in qualsiasi scuola italiana: nido, infanzia, primaria, secondaria, università o formazione professionale.', 'Insegni qualsiasi materia: italiano, matematica, scienze, lingue, sostegno, arte, educazione fisica e qualsiasi altra.', 'Sei stanco di sacrificare i week-end per pianificazioni e correzioni infinite.', "Vuoi usare l'intelligenza artificiale ma non sai da dove iniziare nel contesto scolastico italiano reale.", 'Vuoi tornare a fare quello per cui hai scelto questa professione: fare la differenza.', "Vuoi un metodo operativo, non l'ennesimo corso teorico."].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-76">
                  <div className="mt-1 bg-gold-500/20 p-1.5 rounded-full shrink-0" data-id="element-77">
                    <Check className="w-5 h-5 text-gold-400" data-id="element-78" />
                  </div>
                  <span className="text-lg text-gray-300 leading-relaxed" data-id="element-79">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Why this is different */}
        <div className="mb-20" data-id="element-80">
          <div className="text-center mb-10" data-id="element-81">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-82">
              Perché questo sistema è diverso
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-83">
              Niente corsi teorici, niente abbonamenti, niente software costosi.
              Solo strumenti pronti da usare con l'intelligenza artificiale
              gratuitamente.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-84">
            {differentiators.map((item, i) => <div key={i} className="glass-panel p-6 rounded-2xl border-white/5 hover:border-white/10 transition-colors" data-id="element-85">
                <div className="w-12 h-12 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4" data-id="element-86">
                  <item.icon className="w-6 h-6 text-gold-400" data-id="element-87" />
                </div>
                <h3 className="text-lg font-black text-white mb-3 tracking-wider" data-id="element-88">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-89">{item.desc}</p>
              </div>)}
          </div>
        </div>

        {/* 6 Tomi */}
        <div className="mb-20" data-id="element-90">
          <div className="text-center mb-12" data-id="element-91">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-92">
              I 6 Tomi del Sistema Completo <br data-id="element-93" />
              <span className="gold-gradient-text" data-id="element-94">"Docente PRO"</span>
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-95">
              Strumenti digitali in PDF — pronti da consultare su tablet,
              cellulare o stampati.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-96">
            {tomos.map((item, i) => <div key={i} className="glass-panel p-8 rounded-2xl relative overflow-hidden group" data-id="element-97">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 rounded-full blur-3xl group-hover:bg-gold-500/10 transition-colors blur-glow" data-id="element-98"></div>
                <div className="flex items-center gap-4 mb-5 relative z-10" data-id="element-99">
                  <div className="w-14 h-14 rounded-xl bg-dark-800 border border-white/10 flex items-center justify-center shrink-0" data-id="element-100">
                    <item.icon className="w-7 h-7 text-gold-400" data-id="element-101" />
                  </div>
                  <span className="text-xs font-bold text-gold-400 uppercase tracking-widest bg-gold-500/10 px-3 py-1 rounded-full border border-gold-500/20" data-id="element-102">
                    Tomo {item.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-103">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed relative z-10 text-sm" data-id="element-104">
                  {item.desc}
                </p>
                <p className="mt-4 text-xs text-emerald-400 font-bold relative z-10" data-id="element-105">
                  Valore reale: €47
                </p>
              </div>)}
          </div>
        </div>

        {/* Carousel — sfoglia il materiale */}
        <div className="mb-24" data-id="element-106">
          <div className="text-center mb-10" data-id="element-107">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-108">
              Sfoglia il materiale all'interno
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-109">
              Un'anteprima reale di quello che troverai nei 6 Tomi.
            </p>
            <p className="text-gold-400 font-medium mt-3" data-id="element-110">
              {currentSlide + 1} / {carouselSlides.length}
            </p>
          </div>

          <div className="relative max-w-2xl mx-auto" data-id="element-111">
            <button onClick={prevSlide} aria-label="Pagina precedente" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 hover:border-gold-500/30 transition-colors z-20 shadow-xl" data-id="element-112">
              <ChevronLeft className="w-6 h-6" data-id="element-113" />
            </button>

            <button onClick={nextSlide} aria-label="Pagina successiva" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 hover:border-gold-500/30 transition-colors z-20 shadow-xl" data-id="element-114">
              <ChevronRight className="w-6 h-6" data-id="element-115" />
            </button>

            <div className="overflow-hidden rounded-2xl glass-panel border-gold-500/20 relative bg-dark-800" data-id="element-116">
              <div className="flex transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-117">
                {carouselSlides.map((slide, i) => <div key={i} className="min-w-full relative" data-id="element-118">
                    <img src={slide.image} alt={slide.title} loading="lazy" className="w-full h-auto object-contain bg-white" data-id="element-119" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-6 pt-20" data-id="element-120">
                      <p className="text-xs uppercase tracking-widest text-gold-400 font-bold mb-1" data-id="element-121">
                        {slide.subtitle}
                      </p>
                      <h3 className="text-lg md:text-xl font-bold text-white leading-tight" data-id="element-122">
                        {slide.title}
                      </h3>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6" data-id="element-123">
              {carouselSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Vai alla pagina ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-gold-400 w-8' : 'bg-white/20 hover:bg-white/40 w-2'}`} data-id="element-124" />)}
            </div>
          </div>
        </div>

        {/* Bonuses */}
        <div className="mb-24 relative cv-auto" data-id="element-125">
          <div className="absolute inset-0 bg-gold-500/5 blur-[120px] rounded-full pointer-events-none blur-glow" data-id="element-126"></div>
          <div className="glass-panel border-gold-500/20 p-8 md:p-12 rounded-3xl relative z-10" data-id="element-127">
            <div className="text-center mb-12" data-id="element-128">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-129">
                6 Bonus Esclusivi Inclusi
              </h2>
              <p className="text-gold-400 font-medium text-lg uppercase tracking-wider" data-id="element-130">
                (Senza costi extra)
              </p>
              <div className="inline-block mt-4 px-6 py-2 rounded-full bg-dark-800 border border-white/10" data-id="element-131">
                <p className="text-gray-300 font-semibold" data-id="element-132">
                  Valore totale dei bonus:{' '}
                  <span className="text-white" data-id="element-133">€ 102</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-134">
              {bonuses.map((bonus, i) => <div key={i} className="flex flex-col sm:flex-row items-start gap-5 p-6 rounded-2xl bg-dark-800/80 border border-white/5 hover:border-gold-500/30 transition-colors" data-id="element-135">
                  <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center shrink-0 border border-gold-500/20" data-id="element-136">
                    <bonus.icon className="w-6 h-6 text-gold-400" data-id="element-137" />
                  </div>
                  <div className="flex-1" data-id="element-138">
                    <div className="flex items-center justify-between gap-2 mb-2" data-id="element-139">
                      <h4 className="text-white font-bold text-lg leading-tight" data-id="element-140">
                        {bonus.title}
                      </h4>
                      <span className="px-2.5 py-1 text-[10px] font-black bg-emerald-500/20 text-emerald-400 rounded uppercase tracking-wider shrink-0" data-id="element-141">
                        Gratis
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-142">
                      {bonus.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div id="offerta" className="mb-16 text-center scroll-mt-8" data-id="element-143">
          <h2 className="text-3xl font-bold text-white mb-10" data-id="element-144">
            L'Offerta Completa "Docente PRO"
          </h2>

          <div className="inline-block w-full max-w-2xl glass-panel p-8 md:p-12 rounded-3xl border-gold-500/40 relative overflow-hidden text-left shadow-[0_0_50px_rgba(251,191,36,0.05)]" data-id="element-145">
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-12 py-2 rotate-45 translate-x-10 translate-y-6 shadow-lg z-20 tracking-wider" data-id="element-146">
              93% OFF
            </div>

            <div className="flex justify-center mb-8" data-id="element-147">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/30 text-gold-400 text-sm font-bold tracking-wide uppercase" data-id="element-148">
                <Star className="w-4 h-4 fill-current" data-id="element-149" />
                LA SCELTA PIÙ POPOLARE
              </div>
            </div>

            <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-6 border-b border-white/10 pb-4" data-id="element-150">
              VALORE TOTALE DEI CONTENUTI:
            </p>

            <ul className="space-y-4 mb-8" data-id="element-151">
              {[{
              name: 'Tomo 1 — Fondamenti e Prompt di Intelligenza Artificiale',
              price: '€ 47'
            }, {
              name: 'Tomo 2 — Pianificazione Totale',
              price: '€ 47'
            }, {
              name: 'Tomo 3 — Laboratorio di Classe Creativa',
              price: '€ 47'
            }, {
              name: 'Tomo 4 — Correggere Meno, Valutare Meglio',
              price: '€ 47'
            }, {
              name: 'Tomo 5 — Il Docente Inclusivo',
              price: '€ 47'
            }, {
              name: 'Tomo 6 — Produttività Docente',
              price: '€ 47'
            }, {
              name: '6 Bonus Esclusivi',
              price: '€ 102'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-300" data-id="element-152">
                  <span className="flex items-center gap-2" data-id="element-153">
                    <Check className="w-4 h-4 text-emerald-500" data-id="element-154" />
                    {item.name}
                  </span>
                  <span className="font-medium" data-id="element-155">{item.price}</span>
                </li>)}
            </ul>

            <div className="border-t border-white/10 pt-6 mb-8 flex justify-between items-center" data-id="element-156">
              <span className="text-gray-400 font-medium" data-id="element-157">VALORE REALE:</span>
              <span className="text-xl text-gray-500 line-through decoration-red-500/50 decoration-2 font-bold" data-id="element-158">
                € 384,00
              </span>
            </div>

            <div className="text-center space-y-2 mb-8 bg-dark-900/50 p-6 rounded-2xl border border-white/5" data-id="element-159">
              <p className="text-gold-400 font-bold tracking-widest uppercase text-sm" data-id="element-160">
                SOLO OGGI
              </p>
              <div className="flex items-start justify-center gap-1" data-id="element-161">
                <span className="text-3xl font-bold text-white mt-2" data-id="element-162">€</span>
                <span className="text-7xl font-black text-white tracking-tighter" data-id="element-163">
                  19
                </span>
                <span className="text-3xl font-bold text-white mt-2" data-id="element-164">,90</span>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 text-sm text-gray-400 font-medium mb-8 text-center" data-id="element-165">
              <p data-id="element-166">💳 Pagamento unico • ⚡ Accesso immediato</p>
              <p className="text-red-400 flex items-center gap-1.5 font-semibold" data-id="element-167">
                <Clock className="w-4 h-4" data-id="element-168" /> Solo per i primi 200 docenti — poi
                torna a €47
              </p>
            </div>

            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)] no-underline" data-id="element-169">
              <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-170">
                ✅ ACQUISTA ORA — € 19,90
              </span>
            </a>
            <p className="mt-4 text-sm text-center text-gray-400 font-medium" data-id="element-171">
              ⚡ Accesso Immediato • 💳 Un solo Pagamento • 🛡️ Garanzia 7 Giorni
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-gray-400 font-medium" data-id="element-172">
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-173">
                🔒 Acquisto sicuro
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-174">
                📥 Download immediato
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-175">
                💳 Pagamento unico €19,90
              </div>
              <div className="flex items-center gap-2 justify-center bg-dark-800/50 rounded-lg p-2 border border-white/5" data-id="element-176">
                🛡️ Garanzia 7 giorni
              </div>
            </div>
          </div>
        </div>

        {/* Guarantee */}
        <div className="max-w-3xl mx-auto glass-panel p-8 md:p-10 rounded-3xl flex flex-col md:flex-row items-center gap-8 text-center md:text-left border-emerald-500/30 mb-20 relative overflow-hidden" data-id="element-177">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none blur-glow" data-id="element-178"></div>
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 border border-emerald-500/20 relative z-10" data-id="element-179">
            <ShieldCheck className="w-10 h-10 text-emerald-400" data-id="element-180" />
          </div>
          <div className="relative z-10" data-id="element-181">
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-182">
              GARANZIA TOTALE 7 GIORNI O RIMBORSO
            </h3>
            <p className="text-gray-300 text-base leading-relaxed" data-id="element-183">
              Scarica tutto. Leggi tutto. Usalo nelle tue classi. Se entro 7
              giorni non hai risparmiato almeno 2 ore di lavoro a settimana, se
              non ti senti più organizzato, più veloce e più efficiente con
              l'intelligenza artificiale, ti restituiamo ogni centesimo.{' '}
              <span className="text-white font-bold" data-id="element-184">
                Nessuna domanda. Nessuna burocrazia.
              </span>{' '}
              Il rischio è completamente nostro.
            </p>
          </div>
        </div>

        {/* Mission */}
        <div className="max-w-3xl mx-auto mb-20 text-center cv-auto" data-id="element-185">
          <div className="relative w-64 h-64 mx-auto mb-8" data-id="element-186">
            <div className="absolute inset-0 bg-gold-500/10 blur-[60px] rounded-full pointer-events-none blur-glow" data-id="element-187"></div>
            <img src={HERO_IMAGE} alt="Docente PRO Bundle" width={1080} height={1080} loading="lazy" className="relative z-10 w-full h-full object-contain drop-shadow-2xl" data-id="element-188" />
          </div>
          <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-widest" data-id="element-189">
            Docente PRO — La nostra missione
          </h3>
          <p className="text-xl md:text-2xl text-gray-400 italic leading-relaxed font-serif" data-id="element-190">
            "Abbiamo creato questo sistema per i docenti italiani che lavorano
            duro ma non vedono i risultati nel loro tempo libero e nel loro
            benessere professionale. Niente teoria, niente corsi costosi. Solo
            strumenti pratici per usare l'intelligenza artificiale e gestire il
            tuo lavoro come un professionista vero."
          </p>
        </div>

        {/* Comments / Reviews from real users — lazy + content-visibility for mobile perf */}
        <div className="cv-auto" data-id="element-191">
          <Suspense fallback={<div className="h-[600px]" data-id="element-193" />} data-id="element-192">
            <CommentsSection data-id="element-194" />
          </Suspense>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mb-20 cv-auto" data-id="element-195">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-196">
            Domande Frequenti
          </h2>
          <div className="space-y-4" data-id="element-197">
            {faqs.map((faq, index) => <div key={index} className="glass-panel rounded-xl border-white/5 overflow-hidden transition-all duration-300" data-id="element-198">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-white/[0.02] transition-colors" data-id="element-199">
                  <span className="text-lg font-semibold text-white pr-4" data-id="element-200">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gold-400 shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-201" />
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-202">
                  <p className="text-gray-400 leading-relaxed border-t border-white/5 pt-4" data-id="element-203">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mb-10" data-id="element-204">
          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia di 7 Giorni" scrollTo="offerta" data-id="element-205">
            ✅ SÌ! VOGLIO RECUPERARE IL MIO TEMPO ADESSO
          </CTAButton>
          <p className="text-center text-gray-400 max-w-xl mx-auto mt-4 text-sm" data-id="element-206">
            Unisciti ai docenti italiani che hanno già scelto di lavorare meno,
            insegnare meglio e vivere di più.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-white/5 pt-12 pb-8 px-4 text-center" data-id="element-207">
        <div className="max-w-3xl mx-auto space-y-6" data-id="element-208">
          <div className="flex items-center justify-center gap-2 text-white font-bold text-lg" data-id="element-209">
            <GraduationCap className="w-5 h-5 text-gold-400" data-id="element-210" /> Docente PRO —
            Sistema IA Universale
          </div>
          <p className="text-gray-400 text-sm leading-relaxed max-w-xl mx-auto" data-id="element-211">
            Il metodo definitivo per insegnare senza improvvisare. Strumenti
            pratici per docenti che vogliono risultati concreti.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-500" data-id="element-212">
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-213">
              🔒 Privacy Policy
            </a>
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-214">
              Termini e Condizioni
            </a>
            <a href="#" className="hover:text-gold-400 transition-colors" data-id="element-215">
              Cookie Policy
            </a>
          </div>
          <p className="text-gray-600 text-xs pt-4" data-id="element-216">
            © Docente PRO 2026 — Tutti i diritti riservati. Prodotto digitale —
            PDF scaricabile immediatamente.
          </p>
          <p className="text-gray-600 text-[11px] max-w-2xl mx-auto leading-relaxed pt-2" data-id="element-217">
            I risultati descritti sono esempi reali ma non garantiti. I
            risultati individuali possono variare in base all'utilizzo.
          </p>
        </div>
      </footer>

      <Suspense fallback={null} data-id="element-218">
        <SocialProofPopup data-id="element-219" />
      </Suspense>
    </div>;
}